import React, { useState, useEffect, useRef } from 'react';
import { API } from 'aws-amplify';
import moment from 'moment-timezone';
import qs from 'querystring';
import ApolloClient from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { HttpLink } from 'apollo-link-http';
import { makeStyles, useTheme } from '@material-ui/styles';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Header from './components/Header';
import useSnack from 'hooks/useSnack';
import Form from 'components/form';
import Button from 'components/common/Button';
import withConfirm from '../../hooks/withConfirm';
import Input from '../../components/form/Input';
import Radio from 'components/form/Radio';
import Divider from './components/CustomDivider';
import CartSummary from '../parent/subscription/components/CartSummary';
import Link from '@material-ui/core/Link';

const FormItem = Form.FormItem;

const normalizeEmail = (v, prev) => {
  if (v === prev) {
    return v;
  }
  return v.toLowerCase().trim();
};

const useStyles = makeStyles((theme) => ({
  main: {
    [theme.breakpoints.down('md')]: {},
  },
  sectionTitle: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(3),
  },
  payment: {
    '& h3': {
      textAlign: 'left',
    },
  },
}));

const Checkout = ({ product, history, location, confirm, form }) => {
  const classes = useStyles();
  const snack = useSnack();
  const { getFieldDecorator } = form;
  const [checkoutResult, setResult] = useState();
  const query = qs.parse(location.search.substr(1));
  const theme = useTheme();
  const rootRef = useRef();

  useEffect(() => {
    if (window.Intercom) {
      window.Intercom('boot', { app_id: 'xkovkvvi' });
    }

    return () => {
      window.Intercom('shutdown');
    };
  }, []);

  const processPayment = () => {
    return form
      .validateFields()
      .then((values) => {
        return API.post('public', '/users/checkout', {
          body: {
            ...values,
            timezone: moment.tz.guess(),
            planId: query.plan,
          },
        }).then(
          (resp) => {
            setResult(resp);
          },
          (err) => {
            if (err.response) {
              const { message } = err.response.data;
              if (message === 'Parent account exists') {
                confirm(
                  () => {
                    history.push('/login');
                  },
                  {
                    title: 'Account Exists!',
                    description:
                      "We've found an account with the email address used for parent. If you are already registered with us then please login to purchase.",
                    confirmationText: 'Login',
                  },
                );
              } else {
                snack.error(message);
              }
            } else {
              snack.error(err.message);
            }
          },
        );
      })
      .catch((e) => snack.error('Please check your inputs'));
  };

  if (!query.plan) {
    return <Alert color="error">No product selected.</Alert>;
  }

  return (
    <>
      <Header hideNav />
      <main className={classes.main} ref={rootRef}>
        <Grid container>
          <Grid item xs={12} md={4}>
            <Box
              marginTop={{ xs: 2, md: 0 }}
              marginBottom={{ xs: 3, md: 0 }}
              maxWidth={300}
            >
              <Typography gutterBottom variant="h3">
                Start your free <br/>
                {product.trial_days}-day trial
                <span style={{ color: theme.palette.primary.main }}>.</span>
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} lg={5}>
            {checkoutResult ? (
              <Box>
                <Divider />
                <Typography gutterBottom variant="h3">
                  Welcome to WeAdmit
                </Typography>
                <Typography variant="body2">
                  Thank you for choosing WeAdmit. We will follow-up with an email containing all of the
                  necessary onboarding info. Your college admissions journey has
                  just begun!
                </Typography>
              </Box>
            ) : (
              <div>
                <Box mb={5}>
                  <Divider />
                </Box>
                <Box>
                  <CartSummary
                    plan={product}
                  />
                  { product.service_details && (
                    <Box marginTop={3} marginBottom={6}>
                      <div className="rich-text-content" dangerouslySetInnerHTML={{__html: product.service_details }} />
                    </Box>
                  )}
                  <Divider />
                </Box>
                <Typography variant="h3" className={classes.sectionTitle}>
                  Create your account
                </Typography>
                <Box marginBottom={3}>
                  <Typography color="primary" component="span">
                    ☑
                  </Typography>{' '}
                  All fields are required
                </Box>

                <Form>
                  <Box>
                    <Grid container>
                      <Grid item xs={12}>
                        <FormItem>
                          {getFieldDecorator('student.firstName', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter your first name',
                              },
                            ],
                          })(<Input placeholder="First Name" />)}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12}>
                        <FormItem>
                          {getFieldDecorator('student.lastName', {
                            rules: [
                              {
                                required: true,
                                message: 'Please enter your last name',
                              },
                            ],
                          })(<Input placeholder="Last Name" />)}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12}>
                        <FormItem>
                          {getFieldDecorator('student.email', {
                            normalize: normalizeEmail,
                            rules: [
                              {
                                required: true,
                                message: 'Please enter your email address',
                              },
                              {
                                type: 'email',
                                message: 'Please enter a valid email',
                              },
                            ],
                          })(
                            <Input
                              type="email"
                              placeholder="Email"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12}>
                        <FormItem>
                          {getFieldDecorator('student.emailConfirm', {
                            normalize: normalizeEmail,
                            rules: [
                              {
                                required: true,
                                message: 'Please confirm your email address',
                              },
                              {
                                type: 'email',
                                message: 'Please enter a valid email',
                              },
                            ],
                          })(
                            <Input
                              type="email"
                              placeholder="Confirm Email"
                            />,
                          )}
                        </FormItem>
                      </Grid>
                      <Grid item xs={12}>
                        <FormItem>
                          {getFieldDecorator('tos', {
                            rules: [{ required: true, message: 'Please agree to Terms of Use'}]
                          })(
                            <Radio
                              options={[
                                { value: 'yes', label:         <Typography
                                    component="div"
                                    variant="caption"
                                    style={{ fontSize: 14 }}
                                  >
                                    I have read and agree to WeAdmit{' '}
                                    <Link target="_blank" href="https://www.weadmit.com/terms-of-use">
                                      Terms of Use
                                    </Link>{' '}
                                    and{' '}
                                    <Link target="_blank" href="https://www.weadmit.com/privacy-policy">
                                      Privacy Policy
                                    </Link>
                                    .
                                  </Typography>}
                              ]}
                            />
                          )}
                        </FormItem>
                      </Grid>
                    </Grid>
                  </Box>
                </Form>
                <Box>
                  <Button variant="contained" color="primary" onClick={processPayment}>Create Account</Button>
                </Box>
              </div>
            )}
          </Grid>
        </Grid>
      </main>
    </>
  );
};

export default withConfirm(Form.create()(Checkout));
